import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import withAuth from '../../HOC/withAuth';
import { useTranslation } from '../../t';
import C from '../../colors';
import dayjs from 'dayjs';
import qrcode4Service from '../../assets/qrcode4Service.png';
import { buildSupportTimeline } from '../../supportTimeline';

const Support = inject('snackbarStore', 'userStore')(observer(({ snackbarStore, userStore }) => {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [messages, setMessages] = useState([]);
  const [contentError, setContentError] = useState('');
  const [focused, setFocused] = useState(false);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(() => {
    userStore.fetchMessages().then(data => setMessages(Array.isArray(data) ? data : []));
  }, [userStore]);

  const timeline = buildSupportTimeline(messages);

  const handleSubmit = () => {
    if (!content.trim()) { setContentError(t('Content can not be empty')); return; }
    if (content.length > 4000) { setContentError(t('Content is too long')); return; }
    userStore.createMessage(content).then(() => {
      snackbarStore.showSnackbar(t('Message submited successfully'));
      setContent('');
      fetchMessages();
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
  };

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { userStore.resetUnreadReplies?.(); }, [userStore]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* HEADER */}
      <div style={{ padding: '28px 40px 24px', borderBottom: `1px solid ${C.hair}`, background: C.paper, flexShrink: 0 }}>
        <div style={{ fontFamily: C.mono, fontSize: 11, letterSpacing: 1.4, color: C.brand2, textTransform: 'uppercase', fontWeight: 600, marginBottom: 8 }}>
          Workspace · Feedback
        </div>
        <h1 style={{ margin: 0, fontFamily: C.serif, fontSize: 38, fontWeight: 400, color: C.ink, lineHeight: 1 }}>
          意见和投诉
        </h1>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'grid', gridTemplateColumns: '1fr 320px' }}>

        {/* LEFT — message board */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: `1px solid ${C.hair}` }}>

          {/* message history */}
          <div style={{ flex: 1, overflow: 'auto', padding: '24px 36px' }}>
            {timeline.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: C.ink3, fontSize: 14, fontFamily: C.serif, fontSize: 22 }}>
                还没有留言，欢迎提交意见或投诉。
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {timeline.map(item => item.type === 'user' ? (
                  <div data-testid={`support-message-${item.messageId}`} key={item.id} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <div style={{
                        maxWidth: '72%',
                        background: C.ink, color: '#fff',
                        borderRadius: '12px 12px 2px 12px',
                        padding: '12px 16px',
                        fontSize: 14, lineHeight: 1.6,
                      }}>
                        {item.content}
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,.58)', marginTop: 6, textAlign: 'right', fontFamily: C.mono }}>
                          {item.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss') : ''}
                        </div>
                      </div>
                  </div>
                ) : (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <div data-testid={item.isLatestReply ? `support-reply-${item.messageId}` : `support-reply-item-${item.replyId || item.id}`} style={{
                          maxWidth: '72%',
                          background: '#fff', color: C.ink,
                          border: `1px solid ${C.hair}`,
                          borderRadius: '2px 12px 12px 12px',
                          padding: '12px 16px',
                          fontSize: 14, lineHeight: 1.6,
                        }}>
                          {item.content}
                          <div style={{ fontSize: 11, color: C.ink3, marginTop: 6, fontFamily: C.mono }}>
                            图派客服{item.createdAt ? ` · ${dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss')}` : ''}
                          </div>
                        </div>
                  </div>
                ))}
                <div ref={bottomRef}/>
              </div>
            )}
          </div>

          {/* input */}
          <div style={{ padding: '16px 36px 24px', borderTop: `1px solid ${C.hair}`, flexShrink: 0 }}>
            <textarea
              data-testid="support-message-input"
              value={content}
              onChange={e => { setContent(e.target.value); setContentError(''); }}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onKeyDown={handleKeyDown}
              placeholder="欢迎提交意见或投诉，我们会尽快处理并回复…"
              rows={4}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '12px 14px',
                border: `1.5px solid ${contentError ? '#e88c7c' : focused ? C.brand : C.hair}`,
                borderRadius: 10, outline: 'none',
                fontFamily: C.sans, fontSize: 14, color: C.ink,
                background: '#fff', resize: 'none',
                lineHeight: 1.6,
                transition: 'border-color .2s',
              }}
            />
            {contentError && <div style={{ fontSize: 12, color: '#c2412e', marginTop: 4 }}>{contentError}</div>}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
              <span style={{ fontSize: 12, color: C.ink3 }}>⌘ + Enter 发送</span>
              <button data-testid="support-message-submit" onClick={handleSubmit} style={{
                fontFamily: C.sans, fontSize: 14, fontWeight: 500,
                padding: '9px 20px', borderRadius: 8,
                background: C.brand, color: '#fff', border: 'none', cursor: 'pointer',
                boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
              }}>
                {t('Submit')}
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT — WeChat */}
        <div style={{ padding: '32px 28px', display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div>
            <div style={{ fontFamily: C.mono, fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', color: C.ink3, fontWeight: 600, marginBottom: 10 }}>
              微信客服
            </div>
            <a
              href="https://work.weixin.qq.com/kfid/kfcc9c17c2ebccab67c"
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <div style={{
                background: '#fff', border: `1px solid ${C.hair}`, borderRadius: 14,
                padding: '24px 20px', textAlign: 'center',
                cursor: 'pointer', transition: 'border-color .2s',
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.brand}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.hair}
              >
                <img src={qrcode4Service} alt="企业微信客服二维码" style={{
                  width: '100%', borderRadius: 8, marginBottom: 16, display: 'block',
                }}/>
                <div style={{ fontSize: 13, color: C.ink2, lineHeight: 1.6 }}>
                  扫码或点击联系企业微信客服<br/>
                  <span style={{ fontSize: 12, color: C.ink3 }}>工作日 10:00–18:00 在线</span>
                </div>
              </div>
            </a>
          </div>

          <div style={{ background: C.paper2, border: `1px solid ${C.hair}`, borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.ink, marginBottom: 6 }}>温馨提示</div>
            <div style={{ fontSize: 12, color: C.ink3, lineHeight: 1.7 }}>
              · 意见和投诉通常在 1 个工作日内回复<br/>
              · 如需即时沟通请扫码联系企业微信客服<br/>
              · 客服回复后会在此页面显示
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}));

export default withAuth(Support);
